import { LogoutResult, Oauth2Context, SsoLink, TokenInfo, TokenRequestOptions } from "./types";

export type TokenProvider = () => { access_token: string, refresh_token: string } | null | Promise<{ access_token: string, refresh_token: string } | null>;

export type ReportingAuthClientOptions = {
	serviceUrl?: string,
	loginUrl?: string,
	redirectUrl?: string,
	accounting?: number,
	lang?: string
	clientId?: string,
	clientSecret?: string,
	state?: string,
	params?: Record<string, string>,
	tokenProvider?: TokenProvider,
};

export type ResponseType = 'access_token'|'authorization_code';

const DEFAULT_OPTIONS: Partial<ReportingAuthClientOptions> = {
	serviceUrl: null,
	redirectUrl: typeof window !== 'undefined' ? window.location.href : null,
	accounting: -100,
	lang: null
};

export type ReportingAuthErrorBody = {
	status?: number;
	error?: string;
	error_description?: string;
	message: string;
}

export class ReportingAuthClientException extends Error {
	constructor(public readonly data: ReportingAuthErrorBody) {
		super(data.message);
	}
}

/**
 * Builds the auth service URL carrying an accounting.
 *
 * Rights are always resolved per accounting, so endpoints that authorise the caller
 * (delegateToken, impersonate) need one. Only the path segment works: rp-ms-auth reads
 * the accounting in Bootstrap from the URI, before AccountingMiddleware — measured, the
 * ?a= parameter and the rp-accounting header are both ignored on these routes.
 *
 * The gateway recognises the accounting solely as the segment right after /ms, so
 * `<base>/ms/auth` becomes `<base>/ms/<accounting>/auth`. The bare `<domain>/auth` form
 * rp-cli uses is served by a pattern with no accounting group at all and cannot carry
 * one. Throwing there is deliberate: a silent rewrite miss would produce exactly the
 * 401 insufficient_privileges this construction exists to prevent.
 */
export function accountingServiceUrl(serviceUrl: string, accounting?: number | null): string {
	if (accounting == null) return serviceUrl;

	const match = /^(.*)\/ms\/auth\/?$/.exec(serviceUrl);
	if (!match) {
		throw new ReportingAuthClientException({
			message: `serviceUrl "${serviceUrl}" cannot carry an accounting, "<base>/ms/auth" expected`
		});
	}

	return `${match[1]}/ms/${accounting}/auth`;
}

export class ReportingAuthClient {

	public options: ReportingAuthClientOptions = DEFAULT_OPTIONS;
	private response: Response;

	/**
	 * Body of the last response, already parsed. A fetch body may be read only once, so every
	 * method that consumes it parks the result here for readCredentials() to hand out — reading
	 * it a second time throws "Body is unusable", which used to cost rp-cli the tokens it had
	 * just been issued.
	 *
	 * Boxed because the parsed value may legitimately be null, and a body that never held
	 * credentials (token info, an error) parks { value: null } so it is not read again either.
	 */
	private parsedResponse: { value: Oauth2Context | null } | null = null;

	private _expiresAt: number = null;
	get expiresAt() { return this._expiresAt }
	get expiresIn() {
		if (this._expiresAt === null) return null;
		return this._expiresAt - Math.floor(Date.now() / 1000);
	}

	private async getAuthInit(): Promise<RequestInit> {
		if (this.options.tokenProvider) {
			const token = await this.options.tokenProvider();
			return token?.access_token ? { headers: { 'Authorization': `Bearer ${token.access_token}` } } : {};
		}
		return { credentials: 'include' as const };
	}

	private async fetchWithAuth(url: string, init: RequestInit = {}): Promise<Response> {
		const authInit = await this.getAuthInit();
		return fetch(url, {
			...init,
			...authInit,
			headers: {
				...init.headers as Record<string, string>,
				...authInit.headers as Record<string, string>,
			},
		});
	}

	/** Keeps the parked body in step with the response it was read from */
	private setResponse(response: Response): Response {
		this.response = response;
		this.parsedResponse = null;
		return response;
	}

	/**
	 * Reads the error body of the last response. It carries no credentials and the body is spent
	 * afterwards, so it is parked as such. The HTTP status is attached because callers need to
	 * tell a rejected token (401) from a service that is merely unwell (5xx).
	 */
	private async readErrorBody(): Promise<ReportingAuthErrorBody> {
		const body = await this.response.json() as ReportingAuthErrorBody;
		this.parsedResponse = { value: null };
		return { status: this.response.status, ...body };
	}

	private setOptions(options: Partial<ReportingAuthClientOptions>) {
		Object.assign(this.options, options);

		if (!/^(https?|\/)/.test(this.options.serviceUrl)) {
			this.options.serviceUrl = `https://${this.options.serviceUrl}`;
		}
	}

	constructor(options: Partial<ReportingAuthClientOptions>) {
		this.setOptions(options);
	}

	/**
	 * Issues a token for another user. Like impersonate(), the accounting belongs in the
	 * URL: delegateToken checks the caller's Admin right against the accounting of the
	 * request, and a plain login token carries no accounting — so without it even an
	 * administrator is answered insufficient_privileges.
	 */
	async issueToken(options: TokenRequestOptions) {
		const scope = options.scope ? Array.isArray(options.scope) ? options.scope.join(' ') : options.scope : '';
		const accounting = options.accounting ?? this.options.accounting;
		const serviceUrl = accountingServiceUrl(this.options.serviceUrl, accounting);

		this.setResponse(await this.fetchWithAuth(`${serviceUrl}/delegateToken`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(Object.assign({}, this.options.params, {
				'name': options.name || '',
				'username': options.username || '',
				'expiration': options.expiration,
				'client_id': options.client_id || undefined,
				'scope': scope,
				'accounting': accounting
			}))
		}));

		const credentials = await this.response.json() as Oauth2Context;
		this.parsedResponse = { value: credentials };
		return credentials;
	}

	/**
	 * Impersonates the given user. The accounting is required in practice: rights are
	 * always resolved against one, and with no accounting in the URL the service falls
	 * back to accounting 0, where nobody holds the Admin right — so the call answers
	 * 401 even for a legitimate administrator.
	 */
	async impersonate(username: string, accounting?: number) {
		const serviceUrl = accountingServiceUrl(this.options.serviceUrl, accounting);

		const response = await this.fetchWithAuth(`${serviceUrl}/impersonate`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				'username': username,
			})
		});
		if (!response.ok) {
			const err = await response.json();
			throw new ReportingAuthClientException(err);
		}
	}

	/** SSO links of SA users to other instances (RP_SSO_LINKS of the server) */
	async ssoLinks(accounting?: number): Promise<SsoLink[]> {
		const serviceUrl = accountingServiceUrl(this.options.serviceUrl, accounting);

		const response = await this.fetchWithAuth(`${serviceUrl}/ssoLinks`);
		if (!response.ok) {
			const err = await response.json();
			throw new ReportingAuthClientException(err);
		}
		return await response.json();
	}

	/** URL that signs the current SA user in to the instance of an SSO link; valid for a few minutes */
	async ssoLinkUrl(id: number, accounting?: number): Promise<string> {
		const serviceUrl = accountingServiceUrl(this.options.serviceUrl, accounting);

		const response = await this.fetchWithAuth(`${serviceUrl}/ssoLinks/${id}`, {
			method: 'POST',
		});
		if (!response.ok) {
			const err = await response.json();
			throw new ReportingAuthClientException(err);
		}
		return (await response.json()).url;
	}

	async log(action: string, data: { success?: boolean, result?: string, remark?: string } = {}, accounting?: number) {
		await this.fetchWithAuth(`${this.options.serviceUrl}/log`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ action, accounting: accounting ?? this.options.accounting, ...data })
		});
	}

	async stopImpersonate() {
		const response = await this.fetchWithAuth(`${this.options.serviceUrl}/stopImpersonate`, {
			method: 'POST',
		});
		if (!response.ok) {
			const err = await response.json();
			throw new ReportingAuthClientException(err);
		}
	}

	async authorize(authorizationCode: string) {
		this.setResponse(await this.fetchWithAuth(`${this.options.serviceUrl}/token`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(Object.assign({}, this.options.params, {
				'client_id': this.options.clientId || '',
				'client_secret': this.options.clientSecret || '',
				'grant_type': 'code',
				'code': authorizationCode
			}))
		}));
		if (!this.response.ok) {
			throw new ReportingAuthClientException(await this.readErrorBody());
		}
		return this.response;
	}

	redirect(authorizationCode?: string) {
		let url = `${this.options.serviceUrl}/redirect`;
		const queryParams: string[] = [];

		if (this.options.clientId) {
			queryParams.push('client_id=' + encodeURIComponent(this.options.clientId));
		}

		if (this.options.redirectUrl) {
			let redirectUri = this.options.redirectUrl;
			if (authorizationCode) {
				queryParams.push('code=' + encodeURIComponent(authorizationCode));
			}
			if (this.options.state) {
				queryParams.push('state=' + encodeURIComponent(this.options.state));
			}
			queryParams.push('redirect_uri=' + encodeURIComponent(redirectUri));
		}

		url += (url.includes('?') ? '&' : '?') + queryParams.join('&');

		window.location.href = url;
	}

	async getTokenInfo() : Promise<TokenInfo> {
		this.setResponse(await this.fetchWithAuth(`${this.options.serviceUrl}/info`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			},
		}));

		this._expiresAt = null;

		if (!this.response.ok) {
			if (this.response.headers.get('content-type')?.includes('application/json')) {
				throw new ReportingAuthClientException(await this.readErrorBody());
			} else {
				throw new ReportingAuthClientException({
					message: 'Gateway communication error'
				});
			}
		}

		const tokenInfo = (await this.response.json()) as TokenInfo;
		// spent, and it never held credentials — readCredentials() must not read it again
		this.parsedResponse = { value: null };
		if (tokenInfo.expires_in != null) {
			this._expiresAt = Math.floor(Date.now() / 1000) + tokenInfo.expires_in;
		}

		return tokenInfo;
	}

	/**
	 * Revokes the session. A SAML2 session answers with `logout_url` - the caller navigates there so
	 * the identity provider session ends too; every other session answers with an empty body.
	 */
	async logout(): Promise<LogoutResult> {
		const response = await this.fetchWithAuth(`${this.options.serviceUrl}/revoke`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			},
		});
		const text = response.ok ? await response.text() : '';
		try {
			return text ? JSON.parse(text) as LogoutResult : {};
		} catch {
			return {};
		}
	}

	redirectToLogin() {
		if (typeof window === 'undefined') throw new Error('Not in browser environment');
		if (!this.options.loginUrl) throw new Error('Login URL not set');

		let url = this.options.loginUrl;
		const queryParams: string[] = [];

		if (this.options.clientId) {
			queryParams.push('client_id=' + encodeURIComponent(this.options.clientId));
		}

		if (this.options.redirectUrl) {
			queryParams.push('redirect_uri=' + encodeURIComponent(this.options.redirectUrl));
		}

		if (queryParams.length > 0) {
			url += (url.includes('?') ? '&' : '?') + queryParams.join('&');
		}

		window.location.href = url;
	}

	async readCredentials() {
		if (!this.response) return null;
		if (!this.parsedResponse) {
			this.parsedResponse = { value: await this.response.json() as Oauth2Context };
		}
		return this.parsedResponse.value;
	}

	async refreshToken() {
		const body: Record<string, string> = {grant_type: 'refresh_token'};
		if (this.options.tokenProvider) {
			const token = await this.options.tokenProvider();
			if (token?.refresh_token) {
				body.refresh_token = token.refresh_token;
			}
		}

		if (this.options.clientId) body.client_id = this.options.clientId;
		if (this.options.clientSecret) body.client_secret = this.options.clientSecret;

		this.setResponse(await fetch(`${this.options.serviceUrl}/token`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			credentials: body.refresh_token ? 'omit' : 'include',
			body: JSON.stringify(body)
		}));

		if (this.response.ok) {
			const result = await this.response.json() as Oauth2Context;
			this.parsedResponse = { value: result };
			if (result?.expires_in) {
				this._expiresAt = Math.floor(Date.now() / 1000) + result.expires_in;
			}
		} else {
			throw new ReportingAuthClientException(await this.readErrorBody());
		}

		return this.response;
	}

	async revokeToken() {
		return await this.fetchWithAuth(`${this.options.serviceUrl}/revoke`, {
			method: 'GET',
		});
	}

}
